// The standard table pattern (design handoff §2) — the one dense list used by
// the tickets tab, the projects tab and the person tab. Replaces every ad-hoc
// table on those surfaces.
//
//   · mono uppercase 10px column labels, NO filled header band, no vertical rules
//   · 3px left status spine per row, coloured by the row's state
//   · 12px vertical row padding, 1px hairline divider, single-line rows
//   · priority is a pill (P1 red / P2 amber / P3 grey)
//   · status is a DOT + WORD, never a pill
//   · assignee is a 20–22px monogram square, no name text
//   · row hover tint, row click opens detail, in-row buttons stopPropagation
//
// Colour comes from brainTokens only — no raw hex in this file.

import type { DragEvent, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import { PersonAvatar } from "./PersonAvatar";
import type { PillTone } from "./StatusPill";

const c = brainTokens.color;

/** The row's condition, which colours the 3px left spine. */
export type SpineTone = "danger" | "warning" | "success" | "teal" | "neutral" | "muted";

const SPINE: Record<SpineTone, string> = {
  danger: c.brandRed,
  warning: c.warnDot,
  success: c.successDot,
  teal: c.teal,
  neutral: c.border,
  muted: c.borderLight,
};

const DOT: Record<PillTone, string> = {
  success: c.successDot,
  warning: c.warnDot,
  danger: c.dangerText,
  teal: c.teal,
  neutral: c.faint,
};

export function statusDotColor(tone: PillTone): string {
  return DOT[tone] ?? c.faint;
}

const PRIORITY: Record<string, { fg: string; bg: string }> = {
  P1: { fg: c.dangerText, bg: c.dangerBg },
  P2: { fg: c.warnText, bg: c.warnBg },
  P3: { fg: c.muted, bg: c.neutralPillBg },
};

/**
 * Jira's own priority names → the same three looks. Jira has five levels and
 * the pill has three colours; Low/Lowest read as the quiet grey rather than
 * inventing two more tints nobody can tell apart at 11px.
 */
const JIRA_PRIORITY_LOOK: Record<string, keyof typeof PRIORITY> = {
  highest: "P1", blocker: "P1", critical: "P1",
  high: "P2", major: "P2",
  medium: "P3", normal: "P3", low: "P3", lowest: "P3", minor: "P3", trivial: "P3",
};

/**
 * Priority as a pill. A Jira-backed row shows JIRA'S OWN word ("Highest", "High",
 * …) — the register has to agree with the board it mirrors; brain-only rows keep
 * the P1/P2/P3 scale, which is a brain construct.
 */
export function PriorityPill({ priority }: { priority?: string | null }) {
  const raw = (priority || "P3").trim();
  const key = raw.toLowerCase();
  const lookKey = JIRA_PRIORITY_LOOK[key] ?? (raw.toUpperCase() as keyof typeof PRIORITY);
  const look = PRIORITY[lookKey] ?? PRIORITY.P3;
  // Jira names are Title Case words; brain codes stay upper (P1/P2/P3).
  const label = JIRA_PRIORITY_LOOK[key] ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw.toUpperCase();
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flex: "none",
        background: look.bg,
        color: look.fg,
        borderRadius: `${brainTokens.radius.button}px`,
        px: "8px",
        py: "3px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.02em",
        fontFamily: brainTokens.font.sans,
      }}
    >
      {label}
    </Box>
  );
}

/** Status as a dot + word. Never a pill — that is reserved for priority. */
export function StatusDot({ label, tone }: { label: string; tone: PillTone }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box
        aria-hidden
        sx={{ width: 6, height: 6, borderRadius: "50%", flex: "none", background: statusDotColor(tone) }}
      />
      <Box
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: c.ink2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

/** A quiet mono reference — a Jira key, or an em-dash when there is none. */
export function RefCell({ label, href }: { label?: string | null; href?: string | null }) {
  if (!label) return <Box sx={{ color: c.placeholder, fontFamily: brainTokens.font.mono, fontSize: 12.5 }}>—</Box>;
  const sx = {
    fontFamily: brainTokens.font.mono,
    fontSize: 12.5,
    fontWeight: 600,
    color: c.ink4,
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
  };
  if (!href) return <Box sx={sx}>{label}</Box>;
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e: MouseEvent) => e.stopPropagation()}
      sx={{ ...sx, "&:hover": { textDecoration: "underline", color: c.teal } }}
    >
      {label}
    </Box>
  );
}

export interface RegisterTableColumn {
  key: string;
  /** Mono uppercase 10px label. Empty string for the icon/monogram columns. */
  label: string;
  /** Any valid grid-template-columns track, e.g. `minmax(0,1fr)` or `92px`. */
  width: string;
  align?: "start" | "end" | "center";
  /** Hidden under 900px so the row stays single-line on a laptop. */
  collapse?: boolean;
  /** Clickable header when the table passes onSort. */
  sortable?: boolean;
}

export type RegisterTableSortDir = "asc" | "desc";

export interface RegisterTableRow {
  id: string;
  spine: SpineTone;
  /** Cell content keyed by column key. */
  cells: Record<string, ReactNode>;
  onClick?: () => void;
  /** Faded to 60% — used for rows already acted on. */
  faded?: boolean;
  /** Accessible label for the row button. */
  title?: string;
  /** Person tab: the row can be dragged onto the Today rail. */
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  /** Hovering a row previews its landing spot in the rail. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Wrap an in-row control so a click acts on the control and never opens the
 * row's detail. Every interactive thing inside a row must go through this.
 */
export function RowAction({ children }: { children: ReactNode }) {
  return (
    <Box
      onClick={(e: MouseEvent) => e.stopPropagation()}
      onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", minWidth: 0 }}
    >
      {children}
    </Box>
  );
}

/**
 * Pull into today (handoff §6.3). A `+` circle on every register row; once
 * pinned it becomes a filled teal check, and clicking again unpins. This is the
 * *only* way work enters the Today rail — the rail itself has no text input.
 */
export function PullCircle({
  pinned,
  onToggle,
  busy,
  label,
}: {
  pinned: boolean;
  onToggle: () => void;
  busy?: boolean;
  /** What is being pulled, for the accessible name. */
  label: string;
}) {
  return (
    <RowAction>
      <Box
        component="button"
        type="button"
        aria-pressed={pinned}
        disabled={busy}
        title={pinned ? "Remove from today" : "Add to today"}
        aria-label={pinned ? `Remove from today: ${label}` : `Add to today: ${label}`}
        onClick={onToggle}
        sx={{
          width: 28,
          height: 28,
          flex: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          p: 0,
          lineHeight: 1,
          fontSize: 15,
          fontWeight: 600,
          fontFamily: brainTokens.font.sans,
          borderRadius: `${brainTokens.radius.pill}px`,
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.5 : 1,
          background: pinned ? c.teal : "transparent",
          color: pinned ? c.white : c.tealInk,
          border: `1px solid ${pinned ? c.teal : c.border}`,
          transition: "background 0.12s ease, border-color 0.12s ease",
          "&:hover": busy ? undefined : { background: pinned ? c.tealHover : c.tealTint, borderColor: c.teal },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {pinned ? "✓" : "+"}
      </Box>
    </RowAction>
  );
}

/** 20–22px monogram square. No name text — the tooltip carries it. */
export function AssigneeMonogram({ name, size = 21 }: { name?: string | null; size?: number }) {
  if (!name) return <Box sx={{ color: c.placeholder, fontSize: 12, textAlign: "center" }}>—</Box>;
  return (
    <Box title={name} sx={{ display: "inline-flex" }}>
      <PersonAvatar name={name} size={size} />
    </Box>
  );
}

export function RegisterTable({
  columns,
  rows,
  empty = "Nothing here.",
  dense = false,
  sortKey = null,
  sortDir = "asc",
  onSort,
}: {
  columns: RegisterTableColumn[];
  rows: RegisterTableRow[];
  empty?: ReactNode;
  /** Slightly tighter rows for the drawer / side rail. */
  dense?: boolean;
  sortKey?: string | null;
  sortDir?: RegisterTableSortDir;
  onSort?: (key: string) => void;
}) {
  const template = columns.map((col) => col.width).join(" ");
  const collapsed = columns.filter((col) => !col.collapse);
  const collapsedTemplate = collapsed.map((col) => col.width).join(" ");
  const padY = dense ? "8px" : "12px";

  const gridSx = {
    display: "grid",
    gridTemplateColumns: template,
    gap: "14px",
    alignItems: "center",
    "@media (max-width: 900px)": {
      gridTemplateColumns: collapsedTemplate,
      "& .rt-collapse": { display: "none" },
    },
  } as const;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Column labels: mono, uppercase, 10px, no filled band, no vertical rules. */}
      <Box
        sx={{
          ...gridSx,
          px: "12px",
          pb: "6px",
          borderBottom: `1px solid ${c.borderLight3}`,
        }}
      >
        {columns.map((col) => {
          const active = sortKey === col.key;
          const sortable = !!(col.sortable && onSort && col.label);
          const marker = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
          const headerSx = {
            fontFamily: brainTokens.font.mono,
            fontSize: 10,
            fontWeight: active ? 700 : 500,
            letterSpacing: "0.10em",
            textTransform: "uppercase" as const,
            color: active ? c.ink2 : c.faint,
            justifySelf: col.align === "end" ? "end" : col.align === "center" ? "center" : "start",
            whiteSpace: "nowrap" as const,
          };
          if (!sortable) {
            return (
              <Box
                key={col.key}
                component="span"
                className={col.collapse ? "rt-collapse" : undefined}
                sx={headerSx}
              >
                {col.label}
              </Box>
            );
          }
          return (
            <Box
              key={col.key}
              component="button"
              type="button"
              className={col.collapse ? "rt-collapse" : undefined}
              onClick={() => onSort!(col.key)}
              aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
              title={`Sort by ${col.label}`}
              sx={{
                ...headerSx,
                appearance: "none",
                border: "none",
                background: "none",
                p: 0,
                m: 0,
                cursor: "pointer",
                textAlign: "inherit",
                "&:hover": { color: c.ink },
              }}
            >
              {col.label}
              {marker}
            </Box>
          );
        })}
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ px: "12px", py: "16px", fontSize: 13, color: c.muted }}>{empty}</Box>
      ) : (
        rows.map((row) => (
          <Box
            key={row.id}
            role={row.onClick ? "button" : undefined}
            tabIndex={row.onClick ? 0 : undefined}
            // aria-label only names an interactive element; a static row gets a
            // title so the label is still reachable without lying to AT.
            aria-label={row.onClick ? row.title : undefined}
            title={row.onClick ? undefined : row.title}
            draggable={row.draggable || undefined}
            onDragStart={row.onDragStart}
            onDragEnd={row.onDragEnd}
            onMouseEnter={row.onMouseEnter}
            onMouseLeave={row.onMouseLeave}
            onClick={row.onClick}
            onKeyDown={
              row.onClick
                ? (e: KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      row.onClick!();
                    }
                  }
                : undefined
            }
            sx={{
              borderLeft: `3px solid ${SPINE[row.spine]}`,
              borderBottom: `1px solid ${c.borderLight3}`,
              opacity: row.faded ? 0.6 : 1,
              cursor: row.draggable ? "grab" : row.onClick ? "pointer" : "default",
              transition: "background 0.12s ease",
              "&:last-of-type": { borderBottom: "none" },
              "&:hover": { background: row.onClick || row.draggable ? c.panelBg2 : "transparent" },
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <Box sx={{ ...gridSx, px: "12px", py: padY }}>
              {columns.map((col) => (
                <Box
                  key={col.key}
                  className={col.collapse ? "rt-collapse" : undefined}
                  sx={{
                    minWidth: 0,
                    justifySelf: col.align === "end" ? "end" : col.align === "center" ? "center" : "stretch",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      col.align === "end" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
                  }}
                >
                  {row.cells[col.key] ?? null}
                </Box>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}
